import {
  Ban,
  Bell,
  CalendarPlus,
  CheckCircle2,
  Clock,
  Eye,
  FolderInput,
  History,
  Link2,
  Mail,
  MailWarning,
  MessageSquare,
  RotateCw,
  Send,
  XCircle
} from "lucide-react";
import type { FC } from "react";

import { cn } from "@/lib/utils";

import type { ActivityItem, ActivityKind } from "../intakeActivity";
import { clockLabel } from "../model";

const ICON: Partial<Record<ActivityKind, typeof Send>> = {
  sent: Send,
  resent: RotateCw,
  delivered: Mail,
  opened: Eye,
  reminded: Bell,
  bounced: MailWarning,
  revision_requested: MessageSquare,
  submitted: CheckCircle2,
  extended: CalendarPlus,
  revoked: Ban,
  cancelled: XCircle,
  link_requested: Link2,
  accepted: CheckCircle2,
  filed: FolderInput,
  scheduled_reminder: Clock
};

type Props = {
  items: ActivityItem[];
  clientLabel: string;
  className?: string;
};

/** Events on a hairline spine, who did it, when; scheduled ones dimmed. */
export const ActivityList: FC<Props> = ({ items, clientLabel, className }) => (
  <ol className={cn("flex flex-col", className)}>
    {items.length === 0 ? (
      <li className="py-2 text-xs text-muted-foreground">
        Nothing has happened yet; the intake hasn't been sent.
      </li>
    ) : null}
    {items.map((e, i) => {
      const Icon = ICON[e.kind] ?? History;
      const client = e.actor === "client";
      const bad = e.kind === "bounced";
      return (
        <li
          key={e.id}
          className={cn(
            "flex min-h-7 items-start gap-2.5",
            e.scheduled && "opacity-60"
          )}
        >
          <div className="flex w-3.5 shrink-0 flex-col items-center self-stretch">
            <span
              className={cn(
                "mt-0.5 flex size-3.5 items-center justify-center rounded-full",
                bad
                  ? "bg-destructive/15 text-destructive"
                  : client
                  ? "bg-primary/15 text-primary"
                  : "bg-muted text-muted-foreground",
                e.scheduled &&
                  "border border-dashed border-border bg-transparent"
              )}
            >
              <Icon className="size-2" />
            </span>
            {i < items.length - 1 ? (
              <span className="mt-0.5 w-px flex-1 bg-border" />
            ) : null}
          </div>
          <div className="flex min-w-0 flex-1 flex-col pb-2">
            <div className="flex items-baseline gap-2">
              <span className="min-w-0 flex-1 text-xs">
                <span className="text-muted-foreground">
                  {client
                    ? clientLabel
                    : e.actor === "attorney"
                    ? "Firm"
                    : "System"}{" "}
                  ·{" "}
                </span>
                {e.label}
                {e.scheduled ? (
                  <span className="text-muted-foreground"> · scheduled</span>
                ) : null}
              </span>
              <span className="shrink-0 text-xs whitespace-nowrap text-fg-3">
                {clockLabel(e.at)}
              </span>
            </div>
            {e.detail ? (
              <span className="text-xs break-words text-muted-foreground">
                {e.detail}
              </span>
            ) : null}
          </div>
        </li>
      );
    })}
  </ol>
);
